import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";

import { queries } from "@/api";
import { Chip } from "@/components/Chip";
import { LandingCell } from "@/components/FlightRecorder";
import { ErrorNote } from "@/components/ErrorNote";
import { crewLine, crewRoleLabel, formatDateTime, formatHours, formatMinutes, yourSeat } from "@/lib/format";

export const Route = createFileRoute("/airframes/$id/ledger")({
  component: Ledger,
});

/**
 * The airframe's logbook (F478, decision 099): the flights alone, six
 * columns to scan (the date, the route, the pilot with who was aboard, the
 * time enroute, the landing, the limits) and the totals under them. The
 * returns and the timeline are on the History tab.
 */
function Ledger() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const airframe = useQuery(queries.airframe(id));
  const flights = useQuery(queries.flights(id));
  const l = airframe.data?.ledger;
  return (
    <div className="space-y-6">
      <section className="card-flush">
        <div className="card-head">
          <h2 className="text-[1.0714rem] font-semibold">Flights</h2>
          <span className="muted text-xs tnum">{flights.data?.total ?? 0} in the log</span>
        </div>
        <ErrorNote error={flights.error} />
        {flights.data && flights.data.items.length === 0 ? <p className="muted p-5 text-sm">No flight in the log yet.</p> : null}
        {flights.data && flights.data.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table" data-testid="flights-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Route</th>
                  <th>Pilot</th>
                  <th>Enroute</th>
                  <th>Landing</th>
                  <th>Limits</th>
                  <th aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {flights.data.items.map((f) => {
                  const seat = yourSeat(f.crew);
                  const guests = f.crew.filter((c) => c.guest).length;
                  return (
                    <tr key={f.id} data-flight={f.id} className="table-row-link" onClick={() => navigate({ to: "/airframes/$id/flights/$fid", params: { id, fid: f.id } })}>
                      <td className="tnum">
                        <Link to="/airframes/$id/flights/$fid" params={{ id, fid: f.id }} className="row-link" onClick={(e) => e.stopPropagation()} data-testid="open-flight">
                          {formatDateTime(f.takeoff_utc)}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap">
                        <span className="ident">{f.takeoff_icao || "----"}</span>
                        <span className="muted mx-2" aria-hidden="true">
                          ⟶
                        </span>
                        <span className="ident">{f.landing_icao || "----"}</span>
                      </td>
                      <td>
                        {f.pilot ?? <span className="muted">history</span>}
                        {seat ? (
                          <Chip tone="ok" title={crewLine(f.crew)}>
                            you aboard, {crewRoleLabel(seat.role, seat.pilot_seat)}
                          </Chip>
                        ) : guests > 0 ? (
                          <span className="muted block text-xs" title={crewLine(f.crew)} data-testid="flight-aboard">
                            {guests} aboard
                          </span>
                        ) : null}
                      </td>
                      <td className="tnum">{formatMinutes(f.enroute_min)}</td>
                      <td>
                        <LandingCell flight={f} />
                      </td>
                      <td>
                        {f.has_track ? (
                          <Chip tone={f.exceedances > 0 ? "bad" : "ok"}>{f.exceedances === 0 ? "within the limits" : `${f.exceedances} exceeded`}</Chip>
                        ) : (
                          <span className="muted text-xs">not recorded</span>
                        )}
                      </td>
                      <td className="text-right">
                        <span className="row-open" aria-hidden="true">
                          Open ›
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
        {l ? (
          <p className="muted border-t border-[color:var(--line)] px-5 py-3 text-xs tnum" data-testid="logbook-totals">
            {formatHours(l.total_hours)} on the airframe · {l.cycles} flight{l.cycles === 1 ? "" : "s"} · {l.hard_landings ?? 0} hard landing{(l.hard_landings ?? 0) === 1 ? "" : "s"} · {l.distinct_pilots} pilot
            {l.distinct_pilots === 1 ? "" : "s"}
          </p>
        ) : null}
      </section>
    </div>
  );
}
